import { Pagination, Select, SelectContent, SelectItem, SelectTrigger, Stack } from '@landler/component-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { StockData, StockFilterTypeEnum } from '@/api/rest/resources/types/stocks';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { PAGE_SIZE } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { MetadataColumn } from '@/utils/table/mergeColumns';

import { ComingSoon } from '../../../components/ComingSoon';
import { useStocksSummary } from '../../../hooks/useStocksSummary';
import { PortfolioPageLayout } from '../../components/PortfolioPageLayout';
import {
  BuyerNameCell,
  CurrencyCell,
  SellerNameCell,
  StockIssuanceDateCell,
  StockSaleDateCell,
  StockSerialNumberCell,
} from './components/StockCells';

export const StocksPage = () => {
  const defaultWidgetSlotsConfiguration = {
    name: DefaultWidgetNames.default_template_stocks,
    type: WidgetType.t_page,
    widgets: [
      {
        name: 'org_summary_container',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'col',
          spacing: 6,
        },
        widgets: [
          {
            name: 'org_summary_row_1',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              direction: 'row',
              center: true,
              spacing: 6,
              className: 'flex-wrap lg:flex-nowrap items-stretch',
            },
            widgets: [
              {
                name: DefaultWidgetNames.default_total_stocks_issued_card,
                type: WidgetType.o_total_stocks_issued,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_total_stocks_held_card,
                type: WidgetType.o_total_stocks_held,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_total_stocks_sold_card,
                type: WidgetType.o_total_stocks_sold,
                visible: true,
              },
            ],
          },
          {
            name: 'org_summary_row_2',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              direction: 'row',
              center: true,
              spacing: 6,
              className: 'flex-wrap lg:flex-nowrap items-stretch',
            },
            widgets: [
              {
                name: DefaultWidgetNames.default_total_stocks_issued_value_card,
                type: WidgetType.o_total_stocks_issued_value,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_total_stocks_held_value_card,
                type: WidgetType.o_total_stocks_held_value,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_total_stocks_sold_value_card,
                type: WidgetType.o_total_stocks_sold_value,
                visible: true,
              },
            ],
          },
        ],
      },
    ],
  };

  const customWidgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    DefaultWidgetNames.default_template_stocks,
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = { ...defaultWidgetSlotsConfiguration, ...customWidgetSlotsConfiguration };

  return <StocksPageContent widgetSlotsConfiguration={widgetSlotsConfiguration} />;
};

const StocksPageContent = ({
  widgetSlotsConfiguration,
}: {
  widgetSlotsConfiguration: ReturnType<typeof useActiveWidgetSlotConfiguration>;
}) => {
  const { t } = useTranslation();
  const { data } = useStocksSummary();
  const [filterType, setFilterType] = useState<StockFilterTypeEnum>(StockFilterTypeEnum.all);

  // Check if there is any stock data at all
  const hasNoStockData =
    data.stock_summary.total_number_stocks_issued === 0 &&
    data.stock_summary.total_number_stocks_held === 0 &&
    data.stock_summary.total_number_stocks_sold === 0;

  // TODO: MVP-7663 Backend will add stocks array to response
  // Current API only returns summary (aggregates), not individual stock records
  // Once backend serializes stocks, uncomment: const allStocks = [...data.stocks.issued, ...data.stocks.held, ...data.stocks.sold]
  const allStocks = useMemo<StockData[]>(() => [], []);

  const filteredStocks = useMemo(() => {
    if (filterType === StockFilterTypeEnum.all) {
      return allStocks;
    }
    // When backend adds data.stocks, filter like: return data.stocks[filterType]
    return allStocks;
  }, [filterType, allStocks]);

  if (hasNoStockData) {
    return (
      <PortfolioPageLayout title={t('navbar.stocks')} showFilters={false}>
        <ComingSoon />
      </PortfolioPageLayout>
    );
  }

  return (
    <PortfolioPageLayout title={t('navbar.stocks')} showFilters={true}>
      <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
      <StocksTable filterType={filterType} setFilterType={setFilterType} filteredStocks={filteredStocks} />
    </PortfolioPageLayout>
  );
};

export const StocksTable = ({
  filterType,
  setFilterType,
  filteredStocks,
}: {
  filterType: StockFilterTypeEnum;
  setFilterType: (type: StockFilterTypeEnum) => void;
  filteredStocks: StockData[];
}) => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const setCurrentPage = (page: number) => setSearchParams({ page: `${page}` }, { preventScrollReset: true });

  // Pagination calculations
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;

  const totalStocksCount = filteredStocks.length;
  const paginatedStocks = useMemo(() => {
    return filteredStocks.slice(pageStart, pageEnd);
  }, [filteredStocks, pageStart, pageEnd]);

  const filterOptions = [
    { value: StockFilterTypeEnum.all, label: t('shared.stocks.filters.all') },
    { value: StockFilterTypeEnum.issued, label: t('shared.stocks.filters.issued') },
    { value: StockFilterTypeEnum.held, label: t('shared.stocks.filters.held') },
    { value: StockFilterTypeEnum.sold, label: t('shared.stocks.filters.sold') },
  ];

  const currentFilterLabel =
    filterOptions.find((opt) => opt.value === filterType)?.label || t('shared.stocks.filters.all');

  const defaultColumns = useMemo<MetadataColumn<StockData>[]>(() => {
    const getHeader = (
      accessorKey:
        | 'seller_name'
        | 'buyer_name'
        | 'stock_issuance_date'
        | 'stock_sale_date'
        | 'purchase_price'
        | 'eii_purchase_value'
        | 'current_price'
        | 'current_eii_value'
        | 'stock_serial_number',
    ) => (
      <span className='typography-overline max-w-fit whitespace-nowrap leading-4'>
        {t(`shared.stocks.list.headers.${accessorKey}.title`)}
      </span>
    );

    return [
      {
        accessorKey: 'seller_name',
        header: () => getHeader('seller_name'),
        cell: SellerNameCell,
      },
      {
        accessorKey: 'buyer_name',
        header: () => getHeader('buyer_name'),
        cell: BuyerNameCell,
      },
      {
        accessorKey: 'stock_issuance_date',
        header: () => getHeader('stock_issuance_date'),
        cell: StockIssuanceDateCell,
      },
      {
        accessorKey: 'stock_sale_date',
        header: () => getHeader('stock_sale_date'),
        cell: StockSaleDateCell,
      },
      {
        accessorKey: 'purchase_price',
        header: () => getHeader('purchase_price'),
        cell: CurrencyCell,
      },
      {
        accessorKey: 'eii_purchase_value',
        header: () => getHeader('eii_purchase_value'),
        cell: CurrencyCell,
      },
      {
        accessorKey: 'current_price',
        header: () => getHeader('current_price'),
        cell: CurrencyCell,
      },
      {
        accessorKey: 'current_eii_value',
        header: () => getHeader('current_eii_value'),
        cell: CurrencyCell,
      },
      {
        accessorKey: 'stock_serial_number',
        header: () => getHeader('stock_serial_number'),
        cell: StockSerialNumberCell,
      },
    ];
  }, [t]);

  const tooltipContent = {
    seller_name: t('shared.stocks.list.headers.seller_name.tooltip.body'),
    buyer_name: t('shared.stocks.list.headers.buyer_name.tooltip.body'),
    stock_issuance_date: t('shared.stocks.list.headers.stock_issuance_date.tooltip.body'),
    stock_sale_date: t('shared.stocks.list.headers.stock_sale_date.tooltip.body'),
    purchase_price: t('shared.stocks.list.headers.purchase_price.tooltip.body'),
    eii_purchase_value: t('shared.stocks.list.headers.eii_purchase_value.tooltip.body'),
    current_price: t('shared.stocks.list.headers.current_price.tooltip.body'),
    current_eii_value: t('shared.stocks.list.headers.current_eii_value.tooltip.body'),
    stock_serial_number: t('shared.stocks.list.headers.stock_serial_number.tooltip.body'),
  };

  return (
    <Stack spacing={8} data-testid='stocks-list-desktop' className='w-full overflow-hidden scrollbar-hidden'>
      <div className='flex justify-between items-center'>
        <span className='typography-overline'>
          {t('shared.stocks.pagination.title', {
            count: totalStocksCount,
            start: totalStocksCount > 0 ? Math.max(1, pageStart + 1) : 0,
            end: Math.min(pageEnd, totalStocksCount),
          })}
        </span>
        <Select
          value={filterType}
          onChange={(value) => {
            setFilterType(value as StockFilterTypeEnum);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger
            placeholder={currentFilterLabel}
            className='w-60 rounded-full border-primary-100 hover:bg-primary-12 hover:border-primary-hover'
          />
          <SelectContent>
            {filterOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <GenericTable
        data={paginatedStocks}
        pageSize={PAGE_SIZE}
        defaultColumns={defaultColumns}
        showTooltips={true}
        tooltipContent={tooltipContent}
      />
      <Pagination
        className='typography-overline text-text-secondary mx-auto'
        currentPage={currentPage}
        totalCount={totalStocksCount}
        pageSize={PAGE_SIZE}
        onPageChange={(nextPage) => {
          setCurrentPage(nextPage);
        }}
        data-testid='pagination'
      />
    </Stack>
  );
};
